import React, { useState } from 'react';
import { CircularProgress, Typography, Card, Box, Stack } from '@mui/material';
import ReportShell from '../components/ReportShell';
import YearOverlayChart from '../components/YearOverlayChart';
import SeriesToggle, { useToggleSet } from '../components/SeriesToggle';
import { MeasureControls, useMeasure } from '../components/MeasureControls';
import YearBasisToggle from '../components/YearBasisToggle';
import useStats from '../useStats';
import { MONTH_LABELS, channelYoY, channelLabel, channelColour, yearColour } from '../lib/periods';

const MONTH_INDICES = MONTH_LABELS.map((_, i) => i + 1); // 1..12
const EMPTY = { channels: [], years: [], valueAt: () => 0, idxLabel: (i) => MONTH_LABELS[i - 1] };

/**
 * Channel year-on-year as small multiples — one mini chart per channel, years
 * overlaid month-by-month. Years (lines) and channels (panels) toggle on/off.
 */
export default function ChannelYoY() {
  const { stats, loading } = useStats();
  const measure = useMeasure('revenue');
  const [yearBasis, setYearBasis] = useState('calendar');

  const cy = stats ? channelYoY(stats, measure.get, yearBasis) : EMPTY;
  const yearToggle = useToggleSet(cy.years);
  const channelToggle = useToggleSet(cy.channels);
  const latestYear = cy.years.length ? cy.years[cy.years.length - 1] : undefined;
  const shownYears = cy.years.filter((y) => yearToggle.selected.has(y));
  const shownChannels = cy.channels.filter((c) => channelToggle.selected.has(c));

  const controls = (
    <>
      <MeasureControls {...measure} />
      <YearBasisToggle value={yearBasis} onChange={setYearBasis} />
    </>
  );

  let body;
  if (loading || !stats) {
    body = <CircularProgress />;
  } else if (!cy.channels.length) {
    body = <Typography variant="body2" color="text.secondary">No data yet — import orders and Rebuild stats.</Typography>;
  } else {
    body = (
      <>
        <Stack spacing={1} sx={{ mb: 1 }}>
          <SeriesToggle
            label="Years"
            items={cy.years.map((y) => ({ key: y, label: y, colour: yearColour(y, latestYear) }))}
            selected={yearToggle.selected}
            onToggle={yearToggle.toggle}
            onAll={yearToggle.all}
            onNone={yearToggle.none}
          />
          <SeriesToggle
            label="Channels"
            items={cy.channels.map((c) => ({ key: c, label: channelLabel(c), colour: channelColour(c) }))}
            selected={channelToggle.selected}
            onToggle={channelToggle.toggle}
            onAll={channelToggle.all}
            onNone={channelToggle.none}
          />
        </Stack>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {shownChannels.map((c) => (
            <Card key={c} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{channelLabel(c)}</Typography>
              <YearOverlayChart
                years={shownYears}
                indices={MONTH_INDICES}
                idxLabel={cy.idxLabel}
                valueAt={(y, i) => cy.valueAt(c, y, i)}
                money={measure.money}
                height={240}
                latestYear={latestYear}
              />
            </Card>
          ))}
        </Box>
      </>
    );
  }

  return <ReportShell title="Channel year on year" controls={controls}>{body}</ReportShell>;
}
